import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { API } from "@/lib/apiBase";
import { ImageUploadField } from "@/components/ImageUploadField";

const BLANK = {
  name: "", year: 2024, region: "", producer: "", vineyard: "",
  grape_variety: "", alcohol_pct: 13, size_ml: 750, best_drink: "",
  price: 0, market_value: 0, tasting_notes: "", rating: 90, wine_type: "red", image_url: null,
};

export default function WineFormDialog({ trigger, initial, onSaved }) {
  const editing = Boolean(initial?.id);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) setForm(initial || BLANK);
  }, [open, initial]);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        alcohol_pct: Number(form.alcohol_pct),
        size_ml: Number(form.size_ml),
        price: Number(form.price),
        market_value: Number(form.market_value),
        rating: Number(form.rating),
        image_url: form.image_url || null,
      };
      if (editing) {
        await axios.put(`${API}/admin/wines/${initial.id}`, payload);
      } else {
        await axios.post(`${API}/admin/wines`, payload);
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Save failed. Check all fields.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="bg-wine-paper border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="wine-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white/90">
            {editing ? "Edit Wine" : "Add New Wine"}
          </DialogTitle>
          <DialogDescription className="text-white/40 font-body text-sm">
            {editing ? "Update the wine details and save changes." : "Fill in the wine details to add it to the catalog."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FieldInput label="Name" value={form.name} onChange={(v) => update("name", v)} required testId="wf-name" />
          <FieldInput label="Year" type="number" value={form.year} onChange={(v) => update("year", v === "" ? "" : Number(v))} required testId="wf-year" />
          <FieldInput label="Region" value={form.region} onChange={(v) => update("region", v)} required testId="wf-region" />
          <FieldInput label="Producer" value={form.producer} onChange={(v) => update("producer", v)} required testId="wf-producer" />
          <FieldInput label="Vineyard" value={form.vineyard} onChange={(v) => update("vineyard", v)} testId="wf-vineyard" />
          <FieldInput label="Grape Variety" value={form.grape_variety} onChange={(v) => update("grape_variety", v)} required testId="wf-grape" />
          <FieldInput label="Alcohol %" type="number" value={form.alcohol_pct} onChange={(v) => update("alcohol_pct", v === "" ? "" : Number(v))} step="0.1" testId="wf-alcohol" />
          <FieldInput label="Size (ml)" type="number" value={form.size_ml} onChange={(v) => update("size_ml", v === "" ? "" : Number(v))} testId="wf-size" />
          <FieldInput label="Best Drink" value={form.best_drink} onChange={(v) => update("best_drink", v)} placeholder="e.g. 2025-2040" testId="wf-bestdrink" />
          <div>
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Wine Type</Label>
            <Select value={form.wine_type} onValueChange={(v) => update("wine_type", v)}>
              <SelectTrigger data-testid="wf-type" className="bg-transparent border-white/10 text-white/80 h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wine-paper border-white/10">
                <SelectItem value="red">Red</SelectItem>
                <SelectItem value="white">White</SelectItem>
                <SelectItem value="sparkling">Sparkling</SelectItem>
                <SelectItem value="rose">Rosé</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FieldInput label="Price ($)" type="number" value={form.price} onChange={(v) => update("price", v === "" ? "" : Number(v))} step="0.01" testId="wf-price" />
          <FieldInput label="Market Value ($)" type="number" value={form.market_value} onChange={(v) => update("market_value", v === "" ? "" : Number(v))} step="0.01" testId="wf-mv" />
          <FieldInput label="Rating (0-100)" type="number" value={form.rating} onChange={(v) => update("rating", v === "" ? "" : Number(v))} testId="wf-rating" />
          <div className="md:col-span-2">
            <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">Tasting Notes</Label>
            <Textarea
              value={form.tasting_notes}
              onChange={(e) => update("tasting_notes", e.target.value)}
              rows={3}
              data-testid="wf-notes"
              className="bg-transparent border-white/10 text-white/90 font-body"
            />
          </div>
          <ImageUploadField value={form.image_url} onChange={(v) => update("image_url", v)} testPrefix="wf-image" />
          {error && (
            <p className="md:col-span-2 text-sm text-red-400 font-body" data-testid="wf-error">{error}</p>
          )}
          <DialogFooter className="md:col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-white/70" data-testid="wf-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-merlot hover:bg-merlot-light text-white" data-testid="wf-submit">
              {saving ? "Saving..." : editing ? "Save Changes" : "Create Wine"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, type = "text", step, placeholder, required, testId }) {
  return (
    <div>
      <Label className="font-body text-xs text-white/50 uppercase tracking-wider mb-2 block">{label}</Label>
      <Input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
        className="bg-transparent border-white/10 text-white/90 font-body h-10"
      />
    </div>
  );
}
